import { api } from "@/lib/api";

export type HubTaskPriority = "low" | "normal" | "high";
export type HubTaskStatus = "open" | "done";

/** Where a task came from — meeting follow-ups link back to their meeting. */
export type HubTaskSource = {
  kind: "manual" | "ai" | "meeting" | "calendar" | "mail" | "chat";
  id?: string;
  title?: string;
  url?: string;
};

export type HubTask = {
  id: string;
  title: string;
  notes: string;
  status: HubTaskStatus;
  /** YYYY-MM-DD in the user's time zone, or null. */
  due: string | null;
  priority: HubTaskPriority;
  source: HubTaskSource | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
};

export type HubTaskInput = {
  title?: string;
  notes?: string;
  due?: string | null;
  priority?: HubTaskPriority;
  status?: HubTaskStatus;
};

/** Fired after Tasks change anywhere (Tasks page, Hub AI confirm cards). */
export const TASKS_CHANGED_EVENT = "atw-hub-tasks-changed";

export function announceTasksChanged(): void {
  window.dispatchEvent(new CustomEvent(TASKS_CHANGED_EVENT));
}

export async function fetchTasks(status: "open" | "done" | "all" = "all"): Promise<HubTask[]> {
  const { tasks } = await api<{ tasks: HubTask[] }>(`/api/tasks?status=${status}`, { timeoutMs: 15000 });
  return Array.isArray(tasks) ? tasks : [];
}

export async function createTask(input: HubTaskInput & { title: string }): Promise<HubTask> {
  const { task } = await api<{ task: HubTask }>("/api/tasks", {
    method: "POST",
    body: JSON.stringify(input),
    timeoutMs: 15000,
  });
  return task;
}

export async function updateTask(id: string, input: HubTaskInput): Promise<HubTask> {
  const { task } = await api<{ task: HubTask }>(`/api/tasks/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(input),
    timeoutMs: 15000,
  });
  return task;
}

export async function deleteTask(id: string): Promise<void> {
  await api(`/api/tasks/${encodeURIComponent(id)}`, { method: "DELETE", timeoutMs: 15000 });
}

/** Local calendar date as YYYY-MM-DD (task due dates are plain dates, not instants). */
export function localDay(offsetDays = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

export function formatDue(due: string | null): string {
  if (!due) return "";
  if (due === localDay()) return "Today";
  if (due === localDay(1)) return "Tomorrow";
  if (due === localDay(-1)) return "Yesterday";
  const [y, m, d] = due.split("-").map(Number);
  const date = new Date(y!, (m || 1) - 1, d || 1);
  const sameYear = y === new Date().getFullYear();
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}
